import { FC, MouseEvent, useState } from 'react';
import { Box, MenuItem, MenuList, Typography } from '@mui/material';
import { Accordion, SearchableSelect } from '../../../lib/components';
import { useTranslation } from 'react-i18next';
import {
  CompaniesIcon,
  LanguageIcon,
  HelpIcon,
  LockIcon,
  LaunchIcon,
  AccountIcon,
  HornIcon,
  HeadphonesSupportIcon
} from '../../../lib/icons';
import { CompanySelectionProps } from './companySelection';
import { useOptions } from '../../receipts/detailView/shared/useOptions';
import { useStore } from '../../../hooks/useStore';
import { supportEmail, supportPhone } from '../../../misc/globals';

interface Props extends CompanySelectionProps {
  onSelectLanguage: (lng: 'de' | 'en') => void;
  onPasswordChangeClicked: () => void;
  onManageTwoFactorAuthClicked: () => void;
  onManageAppLoginClicked: () => void;
  onManagePlatformLoginClicked: () => void;
  onKundenWerbenKundenClicked: () => void;
}

const ContactDetail: FC<{ label: string; href: string; value: string }> = ({
  label,
  href,
  value
}) => {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (window.getSelection()?.toString()) {
      event.preventDefault();
    }
  };

  return (
    <Box className="flex flex-col items-start gap-y-0.5">
      <Typography variant="body2" className="text-grey-600">
        {label}
      </Typography>
      <Typography
        component="a"
        href={href}
        draggable={false}
        onClick={handleClick}
        className="-mx-2 rounded-lg px-2 py-1 text-inherit no-underline select-text hover:bg-grey-50"
      >
        {value}
      </Typography>
    </Box>
  );
};

export const MenuItems: FC<Props> = ({
  onSelectLanguage,
  onPasswordChangeClicked,
  onManageTwoFactorAuthClicked,
  onManageAppLoginClicked,
  onManagePlatformLoginClicked,
  onKundenWerbenKundenClicked,
  ...props
}) => {
  const { t } = useTranslation();
  const [selectedMenu, setSelectedMenu] = useState<string>();
  const { authenticationStore } = useStore();

  const companyOptions = useOptions(
    {
      map: (c) => ({
        text: c.name,
        value: c.id
      }),
      filter: (c) => c.status !== 'INACTIVE',
      getItems: { sync: () => props.managedCompanies ?? [] }
    },
    [props.managedCompanies]
  );

  const handleMenuSelect = (menuId: string) => {
    setSelectedMenu(selectedMenu !== menuId ? menuId : undefined);
  };

  const handleCompanySelect = (id: string) => {
    const company = props.managedCompanies?.filter((c) => c.id === id)?.at(0);
    if (company) {
      props.onSelectCompany(company);
    }
  };

  return (
    <Box className="flex flex-col py-3 px-6 gap-y-2">
      {(props.managedCompanies?.length ?? 0) > 1 && (
        <Box className="py-2" data-testid="managed-companies-menu-dropdown">
          <SearchableSelect
            options={companyOptions.sort((a, b) => a.text.localeCompare(b.text))}
            value={String(props.selectedCompany?.id ?? companyOptions.at(0)?.value ?? '')}
            onChange={(id) => handleCompanySelect(String(id))}
            startIcon={<CompaniesIcon />}
            disableClearable
          />
        </Box>
      )}
      <MenuItem onClick={onKundenWerbenKundenClicked} className="w-full rounded-lg !px-0.5 bg-none">
        <HornIcon width={20} height={20} className="mr-4 text-primary-main" />
        {t('referral:PAGE_TITLE')}
      </MenuItem>
      <Accordion
        startIcon={<HelpIcon />}
        headlineData={{ mainText: t('common:HELP_CENTER') }}
        onChange={() => handleMenuSelect('helpcenter')}
        expanded={selectedMenu === 'helpcenter'}
      >
        <MenuList className="flex flex-col items-start justify-center gap-y-3">
          <MenuItem
            onClick={() =>
              window.open(
                authenticationStore.language == 'en'
                  ? 'https://share-eu1.hsforms.com/25TLvRKXzTIiK235G-qc87wfm3j7'
                  : 'https://share-eu1.hsforms.com/2maEkfNjsQkqG8eUDaZhrkgfm3j7',
                '_blank'
              )
            }
            className="w-full pl-10"
          >
            {t('common:CONTACT_FORMULAR')}
            <LaunchIcon className="ml-1" />
          </MenuItem>
          <MenuItem
            onClick={() => window.open(`https://support.pxtra.de/platform`, '_blank')}
            className="w-full pl-10"
          >
            {t('common:FAQ')}
            <LaunchIcon className="ml-1" />
          </MenuItem>
        </MenuList>
      </Accordion>
      <Accordion
        startIcon={<HeadphonesSupportIcon />}
        headlineData={{ mainText: t('common:CONTACT') }}
        onChange={() => handleMenuSelect('contact')}
        expanded={selectedMenu === 'contact'}
      >
        <Box className="flex flex-col gap-y-3 pl-10 text-left" data-testid="contact-details">
          <Typography className="text-[11px] font-semibold uppercase tracking-[0.08em] text-grey-600">
            {t('common:CUSTOMER_SUPPORT')}
          </Typography>
          <ContactDetail
            label={t('company:E_MAIL')}
            href={`mailto:${supportEmail}`}
            value={supportEmail}
          />
          <ContactDetail
            label={`${t('company:TELEPHONE')} · ${t('common:SUPPORT_HOURS')}`}
            href={`tel:${supportPhone.replace(/\s/g, '')}`}
            value={supportPhone}
          />
        </Box>
      </Accordion>
      <Accordion
        startIcon={<LanguageIcon />}
        headlineData={{
          mainText: t('common:LANGUAGE')
        }}
        onClick={() => handleMenuSelect('language')}
        expanded={selectedMenu === 'language'}
      >
        <MenuList className="flex flex-col items-start justify-center gap-y-3">
          <MenuItem onClick={() => onSelectLanguage('de')} className="w-full pl-10">
            {t('common:LANGUAGE_de')}
          </MenuItem>
          <MenuItem onClick={() => onSelectLanguage('en')} className="w-full pl-10">
            {t('common:LANGUAGE_en')}
          </MenuItem>
        </MenuList>
      </Accordion>
      <Accordion
        startIcon={<LockIcon />}
        headlineData={{ mainText: t('company:SECURITY') }}
        onClick={() => handleMenuSelect('security')}
        expanded={selectedMenu === 'security'}
      >
        <MenuList className="flex flex-col items-start justify-center gap-y-3">
          <MenuItem onClick={onManageAppLoginClicked} className="w-full pl-10">
            {t('auth:APP_LOGIN')}
          </MenuItem>
          <MenuItem onClick={onManagePlatformLoginClicked} className="w-full pl-10">
            {t('auth:PLATFORM_LOGIN')}
          </MenuItem>
        </MenuList>
      </Accordion>
      <Accordion
        startIcon={<AccountIcon />}
        headlineData={{
          mainText: t('account:MY_ACCOUNT')
        }}
        onClick={() => handleMenuSelect('settings')}
        expanded={selectedMenu === 'settings'}
      >
        <MenuList className="flex flex-col items-start justify-center gap-y-3">
          <MenuItem onClick={onPasswordChangeClicked} className="w-full pl-10">
            {t('account:SET_NEW_PASSWORD')}
          </MenuItem>
          <MenuItem onClick={onManageTwoFactorAuthClicked} className="w-full pl-10">
            {t('account:MANAGE_2FA')}
          </MenuItem>
        </MenuList>
      </Accordion>
    </Box>
  );
};
